import { PaperClipIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useCallback, useEffect, useState } from 'react'
import { Tooltip } from 'stories/components'

import { isImageFileName } from './ticketHtml'

type Props = {
  name: string
  url: string
  /** Called once when the image fails to load (e.g. expired Asana URL). */
  onExpiredUrl?: () => void
  onDelete?: () => void
}

/**
 * Image attachments render as a thumbnail that opens a full-size lightbox.
 * Non-image files keep the paperclip download link.
 */
export const TicketAttachmentChip = ({ name, url, onExpiredUrl, onDelete }: Props) => {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [failedOnce, setFailedOnce] = useState(false)

  const close = useCallback(() => setLightboxOpen(false), [])

  useEffect(() => {
    if (!lightboxOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [lightboxOpen, close])

  const deleteBtn = onDelete ? (
    <Tooltip message={`Delete ${name}`}>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onDelete()
        }}
        className="inline-flex items-center justify-center p-1 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50"
        aria-label={`Delete ${name}`}
      >
        <TrashIcon className="w-4 h-4" />
      </button>
    </Tooltip>
  ) : null

  if (!isImageFileName(name)) {
    return (
      <div className="inline-flex items-center gap-1">
        <Tooltip message={`Download (${name})`} position="bottom">
          <a
            href={url}
            download={name}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-md text-sm text-gray-700 hover:text-gray-900 transition-colors cursor-pointer group"
          >
            <PaperClipIcon className="w-4 h-4 text-gray-500 group-hover:text-gray-700" />
            <span className="truncate max-w-[200px]">{name}</span>
          </a>
        </Tooltip>
        {deleteBtn}
      </div>
    )
  }

  return (
    <>
      <div className="relative inline-block group">
        <Tooltip message={name} position="bottom">
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            className="block w-24 h-24 rounded-md overflow-hidden border border-gray-300 bg-gray-100 hover:border-shade-blue focus:outline-none focus:ring-2 focus:ring-shade-blue/40"
            aria-label={`View ${name}`}
          >
            <img
              src={url}
              alt={name}
              className="w-full h-full object-cover"
              onError={() => {
                if (failedOnce) return
                setFailedOnce(true)
                onExpiredUrl?.()
              }}
            />
          </button>
        </Tooltip>
        {onDelete && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onDelete()
            }}
            className="absolute -top-2 -right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-white border border-gray-300 text-gray-500 hover:text-red-600 hover:border-red-300 shadow-sm"
            aria-label={`Delete ${name}`}
          >
            <TrashIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 p-4"
          onClick={close}
          role="dialog"
          aria-modal="true"
          aria-label={name}
        >
          <button
            type="button"
            onClick={close}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
            aria-label="Close"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
          <img
            src={url}
            alt={name}
            className="max-w-full max-h-[90vh] object-contain rounded shadow-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  )
}
